<template>
  <div class="tax-details-info">
    <div class="not-approved" v-if="!approved">
      <ContentBlock>
        <div slot="instructions">
          <h3 class="f3">{{ $t('Download Your Tax Form') }}</h3>
          <p
            class="f5"
          >{{ $t('Based on the information you have provided, you will need to complete this official government document provided by the Internal Revenue Service - it is required for us to distribute payments to you.') }}</p>
        </div>
        <div class="button-helper-paragraph">
          <div class="button-helper">
            <UIButton
              v-if="taxFormTemplate"
              :secondary="true"
              target="_blank"
              class="w-100 mb2"
              :href="taxFormTemplate.url"
              rel="nofollow"
            >{{ $t('Download') }} {{ taxFormTemplate.title }}</UIButton>
            <div
              class="f7"
            >{{ $t('Please download the document and fill in your information digitally or by hand.') }}</div>
          </div>
          <div>
            <p
              class="f5"
            >{{ $t('If your tax professional recommends you use a different tax form, please skip downloading this form and instead submit your own below.') }}</p>
          </div>
        </div>
      </ContentBlock>

      <ContentBlock>
        <div slot="instructions">
          <h3 class="f3">{{ $t('Upload Your Completed Tax Form') }}</h3>
          <p class="f5">{{ $t('Complete the Tax Form and re-upload the file.') }}</p>
          <i18n
            path="Need help understanding which tax form to choose? {link} to visit the SpringboardVR Knowledgebase for additional information and resources."
            class="f5"
            tag="p"
          >
            <template #link>
              <UIButton
                link
                href="https://springboardvr.zendesk.com/knowledge/articles/360007497974/en-us?brand_id=6948767"
                rel="noopener"
                target="_blank"
              >{{$t('Click here')}}</UIButton>
            </template>
          </i18n>
        </div>
        <div class="button-helper-paragraph">
          <div class="button-helper">
            <div class="input-group">
              <UIButton :secondary="true" class="mb2 w-100" :disabled="uploading">
                <span v-if="!uploading">{{ $t('Upload Your Completed Tax Form (.pdf)') }}</span>
                <span v-else>{{ $t("Uploading...") }}</span>
              </UIButton>
              <input type="file" class="file-upload" @change="uploadFile" accept=".pdf" />
            </div>
            <div
              class="f7"
            >{{ $t('Please confirm that all required fields have been completed and that all information is correct.') }}</div>
            <br />
            <a
              v-if="organization.taxForm && organization.taxForm.url"
              class="f5 link-style"
              :href="organization.taxForm.url"
              rel="nofollow"
              target="_blank"
            >{{ $t('View Uploaded Tax Form') }}</a>
          </div>
          <p
            class="f5"
          >{{ $t('It will take us approximately 5 business days to review your Tax Form and we will send you an email when it’s approved.') }}</p>
          <p v-if="setup" class="orange f-instructions">
            <UIIcon class="fill-error v-mid dib mr1" name="alert-triangle-fill"></UIIcon>
            {{ $t('Please note, before you start adding your content to SpringboardVR, we need you to fill out your tax form.') }}
            <br />
            {{ $t('You can always skip this step but you won’t be able to publish any title.') }}
          </p>
        </div>
      </ContentBlock>
    </div>

    <div class="approved" v-if="approved">
      <ContentBlock v-if="organization.taxForm.status === 'approved'">
        <div slot="instructions">
          <h3 class="f3">{{ $t('Tax Form') }}</h3>
          <i18n
            path="If you have any changes to your tax status, please contact us at {supportEmail}."
            tag="p"
            class="f5"
          >
            <a
              slot="supportEmail"
              class="link-style"
              href="mailto:support@springboardvr.com"
              rel="nofollow"
            >support@springboardvr.com</a>
          </i18n>
        </div>
        <div class="all-done">
          <p class="f5">{{ $t('Your tax form has been approved!') }}</p>
          <br />
          <a
            class="f5 link-style"
            :href="organization.taxForm.url"
            rel="nofollow"
            target="_blank"
          >{{ $t('View Tax Form') }}</a>
        </div>
      </ContentBlock>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import ContentBlock from '@/core/shared/components/ContentBlock';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIIcon from '@/core/shared/components/ui/UIIcon';

@Component({
  components: {
    ContentBlock,
    UIButton,
    UIIcon,
  },
  props: {
    organization: { type: Object, required: true },
    uploading: { type: Boolean, required: true },
    setup: { type: Boolean, default: false },
  },
})
export default class TaxDetailsInfo extends Vue {
  get approved () {
    return this.organization.taxForm && this.organization.taxForm.status === 'approved';
  }

  get taxFormTemplate () {
    if (this.organization.country === 'US') {
      return {
        title: 'W9',
        url: 'https://www.irs.gov/pub/irs-pdf/fw9.pdf',
      };
    } else if (this.organization.businessType === 'individual') {
      return {
        title: 'W-8BEN',
        url: 'https://www.irs.gov/pub/irs-pdf/fw8ben.pdf',
      };
    } else {
      return {
        title: 'W-8BEN-E',
        url: 'https://www.irs.gov/pub/irs-pdf/fw8bene.pdf',
      };
    }
  }

  uploadFile (e) {
    this.$emit('uploadTaxForm', e.target.files[0]);
    this.fileName = e.target.files[0].name;
    e.target.value = null;
  }
}
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.button-helper-paragraph {
  display: grid;
  grid-gap: var(--spacingMd);

  @media (--desktopXL) {
    grid-template-columns: repeat(2, 1fr);
  }

  & .ui-button {
    white-space: normal;
  }

  & .input-group {
    position: relative;
  }

  & .file-name {
    margin-top: var(--spacingXS);
  }

  & .file-upload {
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
